// Google and Apple hand us a short-lived ID token (JWT) at sign-in. Onboarding
// captures it at Step 1 but only spends it at Step 6 (Connect Bank → register),
// and the onboarding state is persisted to localStorage — so a talent can leave
// and come back hours later. Those tokens expire ~1h after issuance, so by the
// time they reach Connect Bank the stored token may be stale, and the server
// rejects it (invalid_google_credential / invalid_apple_token).
//
// This reads the token's `exp` client-side so we can re-mint a fresh token
// BEFORE sending, instead of dead-ending on a server rejection. It does NOT
// verify the signature — that is the server's job; this is a UX guard only.

function base64UrlDecode(segment: string): string {
  let s = segment.replace(/-/g, "+").replace(/_/g, "/");
  while (s.length % 4) s += "=";
  return atob(s);
}

// Returns true when the token is missing, unreadable, already expired, or due
// to expire within `skewSeconds` (default 120s — enough headroom that a token
// that looks valid now won't die mid request). A token with no `exp` claim is
// treated as still valid here and left for the server to judge.
export function isTokenExpiringSoon(
  token: string | null | undefined,
  skewSeconds = 120
): boolean {
  if (!token) return true;
  try {
    const payloadSegment = token.split(".")[1];
    if (!payloadSegment) return true;
    const payload = JSON.parse(base64UrlDecode(payloadSegment));
    if (typeof payload.exp !== "number") return false;
    const nowSeconds = Date.now() / 1000;
    return payload.exp <= nowSeconds + skewSeconds;
  } catch {
    return true;
  }
}

// The token's `email` claim, or "" when there is none or it can't be read.
// The server creates the account with this same address, so sign-up uses it
// to label the profile photo it leaves on the device for that one account
// (2026-10-10). Unverified, like the expiry check above.
export function tokenEmail(token: string | null | undefined): string {
  try {
    const payload = JSON.parse(base64UrlDecode(String(token).split(".")[1] || ""));
    return typeof payload.email === "string" ? payload.email : "";
  } catch {
    return "";
  }
}
